import { faMagnifyingGlass, faXmark } from "@fortawesome/free-solid-svg-icons";
import Skeleton from "react-loading-skeleton";
import PropTypes from "prop-types";
import {
  TextInput,
  FieldControlSkeleton,
  SearchContainer,
  SearchIcon,
  SearchResetIcon,
  SearchIndicatorsContainer,
  FieldError,
} from "../styles";
import { useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/pro-solid-svg-icons";

// Mirrors SearchIndicatorsContainer's gap/padding and the icon sizes in
// ../styles.js.
const ICON_GAP = 12;
const INDICATORS_PADDING_RIGHT = 15;

const iconSizeFor = (variant) => (variant === "small" ? 12 : 14);

// Keeps typed text from running under the absolutely positioned icon overlay.
const reservedInputPadding = ({ variant, hasError }) => {
  const iconSize = iconSizeFor(variant);

  return (
    INDICATORS_PADDING_RIGHT + iconSize + (hasError ? iconSize + ICON_GAP : 0)
  );
};

const Search = ({
  id,
  name,
  type,
  value = "",
  onChange,
  placeholder = "",
  styles = {
    container: {},
    input: {},
    cancelButton: {},
    icon: {},
    resetIcon: {},
  },
  autoFocus = false,
  variant = "large",
  isDisabled = false,
  isReadOnly = false,
  showSkeleton = false,
  error,
  dataTest,
}) => {
  const inputRef = useRef();
  const normalizedValue = value ?? "";
  const iconSize = iconSizeFor(variant);

  const handleChange = (event) => {
    onChange(event.target.value);
  };

  const handleReset = () => {
    onChange("");
    inputRef?.current?.focus();
  };

  const handleResetKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleReset();
    }
  };

  return (
    <SearchContainer variant={variant} style={{ ...styles.container }}>
      {showSkeleton ? (
        <FieldControlSkeleton type="search" size={variant}>
          <Skeleton />
        </FieldControlSkeleton>
      ) : (
        <>
          <TextInput
            id={id}
            type="search"
            variant={variant}
            disabled={isDisabled}
            readOnly={isReadOnly}
            field={name || "search"}
            name={name || "search"}
            placeholder={placeholder || "Search by..."}
            value={normalizedValue}
            onChange={handleChange}
            style={{
              paddingRight: `${reservedInputPadding({
                variant,
                hasError: Boolean(error),
              })}px`,
              ...styles.input,
            }}
            cancelButtonStyles={styles.cancelButton}
            autoFocus={autoFocus}
            error={error}
            ref={inputRef}
            autoComplete="off"
            data-test={dataTest}
          />
          <SearchIndicatorsContainer variant={variant} disabled={isDisabled}>
            {error && (
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{
                  color: "#BA351C",
                  width: `${iconSize}px`,
                  height: `${iconSize}px`,
                }}
              />
            )}
            {normalizedValue === "" ? (
              <SearchIcon
                icon={faMagnifyingGlass}
                variant={variant}
                disabled={isDisabled}
                style={{ ...styles.icon }}
              />
            ) : (
              <SearchResetIcon
                onClick={handleReset}
                onKeyDown={handleResetKeyDown}
                icon={faXmark}
                variant={variant}
                role="button"
                tabIndex={isDisabled ? -1 : 0}
                aria-label="Clear search"
                aria-hidden={false}
                aria-disabled={isDisabled || undefined}
                style={{ ...styles.resetIcon }}
              />
            )}
          </SearchIndicatorsContainer>
          {!type && (
            <FieldError style={{ position: "absolute", right: "0px" }}>
              {error}
            </FieldError>
          )}
        </>
      )}
    </SearchContainer>
  );
};

Search.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  // Not the input type — the input is always type="search". Truthy only means
  // "rendered inside Field", which renders its own error text.
  type: PropTypes.oneOf(["search"]),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  styles: PropTypes.shape({
    container: PropTypes.object,
    input: PropTypes.object,
    cancelButton: PropTypes.object,
    icon: PropTypes.object,
    resetIcon: PropTypes.object,
  }),
  autoFocus: PropTypes.bool,
  variant: PropTypes.oneOf(["large", "small"]),
  isDisabled: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  showSkeleton: PropTypes.bool,
  error: PropTypes.string,
  dataTest: PropTypes.string,
};

export default Search;
